import React, { Fragment, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';
import { Main } from '@klp/layout';
import { useTranslation } from 'react-i18next';

import { ForvaltningsplanAccess } from '../config';
import type { ConfluenceContent } from '../services/rtkApi';
import { useLazyGetConfluenceContentQuery } from '../services/rtkApi';
import { Heading, Block, Tooltip, Typography, ActionButton } from '@klp/design';
import { Box, Grid, IconButton } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Loader } from '@klp/design/Loader';
import DOMPurify from 'dompurify';
import ReactHtmlParser from 'react-html-parser';
import { contextSelectors } from '@klp/contextSelector';
import { useCurrentUser } from '@klp/auth';

export default function ForvaltningsplanView() {
    const { t } = useTranslation();
    const [zIndex, setZIndex] = useState(0);
    const useStyles = makeStyles((theme) => ({
        datePicker: {
            width: '150px',
        },
        errorContainer: {
            height: '100%',
            width: '100vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexDirection: 'column',
        },
        errorContent: {
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            minHeight: '50%',
            gap: theme.spacing(1),
        },
        actionButtons: {
            position: 'sticky',
            top: '10px',
            backgroundColor: 'white',
            zIndex: zIndex,
            width: 'fit-content',
        },
    }));

    const classes = useStyles();
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const handleDateChange = (date: Date) => {
        setSelectedDate(date);
    };
    const { currentUser } = useCurrentUser();
    const { selectedContext } = useSelector(contextSelectors);

    const [
        getConfluenceContent,
        {
            data: confluenceContentData,
            isError: confluenceContentError,
            isLoading: confluenceContentLoading,
            isFetching: confluenceContentFetching,
        },
    ] = useLazyGetConfluenceContentQuery();

    const htmlContent = (
        html: string,
        confluenceContent: ConfluenceContent = {},
    ) => {
        const appendAdditionalElements = (
            htmlDoc: Document,
            confluenceContentHtml?: ConfluenceContent,
        ) => {
            if (!confluenceContentHtml) return;

            const parser = new DOMParser();
            const stylingDoc = parser.parseFromString(
                confluenceContentHtml?.styling ?? '',
                'text/html',
            );

            stylingDoc
                .querySelectorAll('link[type="text/css"], style')
                .forEach((element) => {
                    htmlDoc.head.appendChild(element.cloneNode(true));
                });

            stylingDoc
                .querySelectorAll('script[type="text/javascript"]')
                .forEach((element) => {
                    htmlDoc.head.appendChild(element.cloneNode(true));
                });
        };

        const processHtml = (htmlString: string): string => {
            const parser = new DOMParser();
            const htmlDoc = parser.parseFromString(htmlString, 'text/html');

            const elements = htmlDoc.querySelectorAll<HTMLElement>(
                '[data-highlight-colour]',
            );

            elements.forEach((element) => {
                const bgColor = element.getAttribute('data-highlight-colour');
                if (bgColor) {
                    element.style.backgroundColor = bgColor;
                }
            });

            const css = `
            html {
                width: 100%;
                height: 100%;
            }
            table {
                width: 100%;
                border: 1px solid lightgrey;
                border-collapse: collapse;
            }

            th, td {
                border: 1px solid lightgrey;
                padding: 8px;
                text-align: left;
            }

            th {
                background-color: #f2f2f2;
            }
            li, ul, p, span {
                font-size: 18px;
            }
              
            li:not(ul li), ul:not(ul ul), span:not(span span) {
                font-size: inherit;
            }
            a {
                color: #06735c;
            }
        `;
            const styleTag = htmlDoc.createElement('style');
            styleTag.innerHTML = css;
            htmlDoc.head.appendChild(styleTag);

            appendAdditionalElements(htmlDoc, confluenceContent);

            return htmlDoc.documentElement.innerHTML;
        };

        const processedHtml = processHtml(html);

        const options = {
            decodeEntities: true,
        };

        return ReactHtmlParser(processedHtml, options);
    };

    const handleScroll = () => {
        if (window.scrollY > 135) {
            setZIndex(1500);
        } else {
            setZIndex(0);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (selectedContext?.id) {
            getConfluenceContent({
                byggNummer: selectedContext?.id,
                aar: selectedDate.getFullYear(),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContext, selectedDate]);

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="forvaltningsplan"
                    label={t('breadcrumbs.forvaltningsplan')}
                />
            </Main.Breadcrumbs>
            {!ForvaltningsplanAccess(currentUser) || !confluenceContentError ? (
                !selectedContext ? (
                    /* Add a better error message / component here */
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <p>{t('forvaltningsplan.noContextSelected')}</p>
                    </div>
                ) : (
                    <Loader
                        isLoading={
                            confluenceContentLoading ||
                            confluenceContentFetching
                        }
                    >
                        {confluenceContentData?.body && (
                            <>
                                <Block paddingTop="tiny">
                                    <Grid
                                        container={true}
                                        direction="row"
                                        justifyContent="flex-start"
                                    >
                                        <Grid item={true}>
                                            <Block>
                                                <Heading
                                                    klpHeadingLevel="ONE"
                                                    klpColor="dark"
                                                    translation="forvaltningsplan.title"
                                                />
                                            </Block>
                                        </Grid>
                                        <Grid item={true}>
                                            <Block
                                                paddingLeft="tiny"
                                                paddingBottom="compact"
                                            >
                                                <Heading
                                                    klpHeadingLevel="ONE"
                                                    klpColor="dark"
                                                >
                                                    {selectedContext?.navn}
                                                </Heading>
                                            </Block>
                                        </Grid>
                                    </Grid>
                                </Block>
                                <Block
                                    className={classes.actionButtons}
                                    paddingTop="tiny"
                                    paddingBottom="tiny"
                                >
                                    <Grid
                                        container={true}
                                        direction="row"
                                        justifyContent="flex-start"
                                        spacing={1}
                                    >
                                        <Grid item={true}>
                                            <Block
                                                className={classes.datePicker}
                                            >
                                                <KeyboardDatePicker
                                                    fullWidth={true}
                                                    allowKeyboardControl={true}
                                                    views={['year']}
                                                    label={t(
                                                        'comments.datePickerLabel',
                                                    )}
                                                    variant="inline"
                                                    inputVariant="outlined"
                                                    InputAdornmentProps={{
                                                        position: 'end',
                                                    }}
                                                    value={selectedDate}
                                                    onChange={(
                                                        date: Date | null,
                                                    ) => {
                                                        if (date) {
                                                            handleDateChange(
                                                                date as Date,
                                                            );
                                                        }
                                                    }}
                                                    maxDate={new Date()}
                                                    minDate={
                                                        new Date(2021, 0, 1)
                                                    }
                                                    autoOk={true}
                                                />
                                            </Block>
                                        </Grid>
                                        <Grid item={true}>
                                            <Block>
                                                <Tooltip
                                                    translation="forvaltningsplan.read"
                                                    title=""
                                                >
                                                    <IconButton
                                                        color="inherit"
                                                        aria-label="Open external confluence view"
                                                        onClick={() =>
                                                            window.open(
                                                                confluenceContentData?.readLink,
                                                                '_blank',
                                                            )
                                                        }
                                                    >
                                                        <Illustration
                                                            icon={
                                                                IconName.EXTERNAL
                                                            }
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </Block>
                                        </Grid>
                                        <Grid item={true}>
                                            <Tooltip
                                                translation="forvaltningsplan.edit"
                                                title=""
                                            >
                                                <IconButton
                                                    color="inherit"
                                                    aria-label="Open external confluence edit view"
                                                    onClick={() =>
                                                        window.open(
                                                            confluenceContentData?.editLink,
                                                            '_blank',
                                                        )
                                                    }
                                                >
                                                    <Illustration
                                                        icon={IconName.EDIT}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </Block>
                                <Block paddingTop="tiny">
                                    {htmlContent(
                                        DOMPurify.sanitize(
                                            confluenceContentData.body,
                                        ),
                                        {
                                            styling:
                                                confluenceContentData.styling,
                                        },
                                    )}
                                </Block>
                            </>
                        )}
                    </Loader>
                )
            ) : (
                <Block className={classes.errorContainer}>
                    <Box className={classes.errorContent}>
                        <Heading
                            klpHeadingLevel="ONE"
                            translation="general.error.title"
                        />

                        <Typography
                            variant="h5"
                            translation="general.error.message"
                        />

                        <ActionButton
                            actionType="button"
                            translation="general.reload"
                            onClick={() => window.location.reload()}
                        />
                    </Box>
                </Block>
            )}
        </Main>
    );
}
